/**
 * ModelPicker — Premium searchable model selector
 *
 * Features:
 * - Live search across all provider models
 * - Provider grouping with icons
 * - Pricing display ($/1M tokens)
 * - Context length indicators
 * - Pinned popular models at top
 * - Keyboard navigation
 * - Click-outside-to-close
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import {
    fetchProviderModels,
    formatPrice,
    PINNED_MODELS,
    type ProviderModel,
} from '../../engine/modelRegistry'

interface ModelPickerProps {
    currentModel: string
    baseUrl?: string
    apiKey?: string
    onSelect: (modelId: string) => void
    onClose: () => void
}

const PROVIDER_ICONS: Record<string, string> = {
    'anthropic': '🟧',
    'openai': '🟢',
    'google': '🔵',
    'meta-llama': '🟣',
    'deepseek': '🔮',
    'mistralai': '🟠',
    'cohere': '🟤',
    'qwen': '⚪',
    'perplexity': '🔷',
    'x-ai': '⚫',
}

function getProviderIcon(modelId: string): string {
    const provider = modelId.split('/')[0]
    return PROVIDER_ICONS[provider] ?? '⬜'
}

function formatContext(ctx?: number): string {
    if (!ctx) return ''
    if (ctx >= 1_000_000) return `${(ctx / 1_000_000).toFixed(1)}M ctx`
    if (ctx >= 1000) return `${Math.round(ctx / 1000)}K ctx`
    return `${ctx} ctx`
}

export function ModelPicker({ currentModel, baseUrl, apiKey, onSelect, onClose }: ModelPickerProps) {
    const [models, setModels] = useState<ProviderModel[]>([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [selectedIndex, setSelectedIndex] = useState(0)
    const searchRef = useRef<HTMLInputElement>(null)
    const listRef = useRef<HTMLDivElement>(null)
    const overlayRef = useRef<HTMLDivElement>(null)

    // Fetch models on mount
    useEffect(() => {
        setLoading(true)
        fetchProviderModels(baseUrl, apiKey).then(m => {
            setModels(m)
            setLoading(false)
        })
    }, [baseUrl, apiKey])

    // Autofocus search
    useEffect(() => {
        searchRef.current?.focus()
    }, [])

    // Click outside to close
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (overlayRef.current && !overlayRef.current.contains(e.target as Node)) {
                onClose()
            }
        }
        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [onClose])

    // Filter models
    const filtered = useMemo(() => {
        if (!search.trim()) return models

        const query = search.toLowerCase().trim()
        const terms = query.split(/\s+/)

        return models.filter(m => {
            const targetStr = `${m.id} ${m.name}`.toLowerCase()
            // Every search term must match
            return terms.every(term => targetStr.includes(term))
        })
    }, [models, search])

    // Keep selection in bounds
    useEffect(() => {
        setSelectedIndex(0)
    }, [search])

    // Scroll selected item into view
    useEffect(() => {
        const list = listRef.current
        if (!list) return
        const selected = list.children[selectedIndex] as HTMLElement | undefined
        selected?.scrollIntoView({ block: 'nearest' })
    }, [selectedIndex])

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setSelectedIndex(i => Math.min(i + 1, filtered.length - 1))
                break
            case 'ArrowUp':
                e.preventDefault()
                setSelectedIndex(i => Math.max(i - 1, 0))
                break
            case 'Enter':
                e.preventDefault()
                if (filtered[selectedIndex]) {
                    onSelect(filtered[selectedIndex].id)
                    onClose()
                }
                break
            case 'Escape':
                e.preventDefault()
                onClose()
                break
        }
    }, [filtered, selectedIndex, onSelect, onClose])

    const handleSelect = useCallback((model: ProviderModel) => {
        onSelect(model.id)
        onClose()
    }, [onSelect, onClose])

    return (
        <div className="model-picker-overlay" ref={overlayRef}>
            <div className="model-picker-header">
                <input
                    ref={searchRef}
                    type="text"
                    className="model-picker-search"
                    placeholder="Search models… (e.g. 'claude', 'gpt', 'gemini')"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={handleKeyDown}
                />
                <button className="model-picker-close" onClick={onClose} title="Close">✕</button>
            </div>

            {loading ? (
                <div className="model-picker-loading">
                    <div className="model-picker-spinner" />
                    <span>Loading models…</span>
                </div>
            ) : (
                <div className="model-picker-list" ref={listRef}>
                    {filtered.length === 0 && (
                        <div className="model-picker-empty">
                            {search
                                ? `No models matching "${search}"`
                                : 'No models available from this provider'}
                        </div>
                    )}
                    {filtered.map((m, i) => {
                        const isPinned = PINNED_MODELS.includes(m.id)
                        const isActive = m.id === currentModel
                        const isSelected = i === selectedIndex

                        return (
                            <button
                                key={m.id}
                                className={[
                                    'model-picker-item',
                                    isActive && 'model-item-active',
                                    isSelected && 'model-item-selected',
                                    isPinned && 'model-item-pinned',
                                ].filter(Boolean).join(' ')}
                                onClick={() => handleSelect(m)}
                                onMouseEnter={() => setSelectedIndex(i)}
                            >
                                <div className="model-item-main">
                                    <span className="model-item-icon">{getProviderIcon(m.id)}</span>
                                    <div className="model-item-info">
                                        <div className="model-item-name-row">
                                            <span className="model-item-name">{m.name}</span>
                                            {isPinned && <span className="model-item-star" title="Popular model">★</span>}
                                            {isActive && <span className="model-item-check">✓</span>}
                                        </div>
                                        <span className="model-item-id">{m.id}</span>
                                    </div>
                                </div>
                                <div className="model-item-meta">
                                    <span className="model-item-price">
                                        {formatPrice(m.pricing.prompt)}
                                        <span className="price-sep">/</span>
                                        {formatPrice(m.pricing.completion)}
                                    </span>
                                    {m.context_length && (
                                        <span className="model-item-ctx">
                                            {formatContext(m.context_length)}
                                        </span>
                                    )}
                                </div>
                            </button>
                        )
                    })}
                </div>
            )}

            <div className="model-picker-footer">
                <span className="model-picker-count">
                    {filtered.length} model{filtered.length !== 1 ? 's' : ''}
                    {search && ` matching "${search}"`}
                </span>
                <span className="model-picker-hint">↑↓ navigate · Enter select · Esc close</span>
            </div>
        </div>
    )
}
